import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, TextField } from '@mui/material';
import { ContactUsRequestSchema, type ContactUsRequest } from '@weft/api-definition';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { useContactUs } from 'apps/app/src/hooks/requests/use-contact-us';
import { useDialog } from 'apps/app/src/hooks/use-dialog';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

type ContactUsDialogProps = {
  children: React.ReactElement;
};

export const ContractUsDialog: React.FC<ContactUsDialogProps> = ({ children }) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const contactUsMutation = useContactUs();

  const methods = useForm<ContactUsRequest>({ resolver: zodResolver(ContactUsRequestSchema) });

  const handleSubmit = methods.handleSubmit(async (data) => {
    await runMutation(
      contactUsMutation,
      data,
      {
        error: 'Failed to send message',
        pending: 'Sending message...',
        success: 'Message sent successfully',
      });
    handleClose();
  });

  return (
    <>
      {React.cloneElement(children, { onClick: handleOpen })}
      <FormProvider {...methods}>
        <VyDialog
          ctaText="Submit"
          label="Contact Us"
          cancelButtonText="Cancel"
          open={isDialogOpen}
          onSubmit={handleSubmit}
        >
          <Stack
            direction="column"
            gap={2}
          >
            <Controller
              control={methods.control}
              name='name'
              render={({ field }) => (
                <TextField
                  {...field}
                  defaultValue={field.value ?? ''}
                  size='small'
                  error={!!methods.formState.errors.name}
                  helperText={methods.formState.errors.name?.message}
                  onChange={field.onChange}
                  label='Name'
                />
              )}
            />
            <Controller
              control={methods.control}
              name='email'
              render={({ field }) => (
                <TextField
                  {...field}
                  defaultValue={field.value ?? ''}
                  size='small'
                  error={!!methods.formState.errors.email}
                  helperText={methods.formState.errors.email?.message}
                  onChange={field.onChange}
                  label='Email'
                />
              )}
            />
            <Controller
              control={methods.control}
              name='message'
              render={({ field }) => (
                <TextField
                  {...field}
                  defaultValue={field.value ?? ''}
                  onChange={field.onChange}
                  error={!!methods.formState.errors.message}
                  helperText={methods.formState.errors.message?.message}
                  size='small'
                  minRows={3}
                  multiline
                  label='Message'
                />
              )}
            />
          </Stack>
        </VyDialog>
      </FormProvider>
    </>
  );
};